import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { EmpresaContextProvider, useEmpresaContext } from './context/EmpresaContext';
import { Toaster } from './components/ui/sonner';
import useDynamicFavicon from './hooks/useDynamicFavicon';

// Pages
import LoginPage from './pages/LoginPage';
import PublicFormPage from './pages/PublicFormPage';
import DashboardPage from './pages/DashboardPage';
import ReclamosPage from './pages/ReclamosPage';
import ProyectosPage from './pages/ProyectosPage';
import UsuariosPage from './pages/UsuariosPage';
import EmpresaPage from './pages/EmpresaPage';
// Módulo Centro de Ayuda (Tickets)
import CentroAyudaTickets from './modules/centroAyuda/TicketsPage';
import CentroAyudaTicketDetalle from './modules/centroAyuda/TicketDetallePage';
import CentroAyudaConfiguracion from './modules/centroAyuda/ConfiguracionPage';
import CentroAyudaEstadisticas from './modules/centroAyuda/EstadisticasPage';
import CentroAyudaAuditoria from './modules/centroAyuda/AuditoriaPage';
import EmpresasPage from './pages/EmpresasPage';
import AuditoriaPage from './pages/AuditoriaPage';
import PerfilPage from './pages/PerfilPage';
import NotificacionesPage from './pages/NotificacionesPage';
import ResolucionPage from './pages/ResolucionPage';
import ResolucionesPage from './pages/ResolucionesPage';
import ConsultaReclamoPage from './pages/ConsultaReclamoPage';
import CambioPasswordObligatorio from './components/CambioPasswordObligatorio';

// Layout
import AdminLayout from './layouts/AdminLayout';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, requiereCambioPassword, setRequiereCambioPassword } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a1a2e]"></div>
      </div>
    );
  }

  if (!user) {
    const empresaCodigo = localStorage.getItem('ldrv_empresa_codigo');
    const loginPath = empresaCodigo ? `/login/${empresaCodigo}` : '/login';
    return <Navigate to={loginPath} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.rol)) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  if (requiereCambioPassword) {
    return (
      <>
        {children}
        <CambioPasswordObligatorio onSuccess={() => setRequiereCambioPassword(false)} />
      </>
    );
  }

  return children;
};

// Home redirect - respeta dominio de empresa
const HomeRedirect = () => {
  const { user, loading: authLoading } = useAuth();
  const { esDominioEmpresa, empresaDominio, loading: empresaLoading } = useEmpresaContext();

  if (authLoading || empresaLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a1a2e]"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  // Si es dominio de empresa, mostrar login de esa empresa directamente
  if (esDominioEmpresa && empresaDominio) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to="/login" replace />;
};

function AppRoutes() {
  const { esDominioEmpresa, empresaDominio, loading: empresaLoading } = useEmpresaContext();
  const { empresa } = useAuth();

  // Favicon dinámico: prioridad dominio > empresa logueada
  const faviconCodigo = esDominioEmpresa ? empresaDominio?.codigo : empresa?.codigo;
  useDynamicFavicon(faviconCodigo);

  if (empresaLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a1a2e]"></div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Home */}
      <Route path="/" element={<HomeRedirect />} />

      {/* Public Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/login/:empresaCodigo" element={<LoginPage />} />

      {esDominioEmpresa && empresaDominio ? (
        <>
          {/* Rutas públicas para dominio de empresa: sin :empresaCodigo en URL */}
          <Route path="/reclamos" element={<PublicFormPage empresaCodigoProp={empresaDominio.codigo} />} />
          <Route path="/consulta" element={<ConsultaReclamoPage empresaCodigoProp={empresaDominio.codigo} />} />
        </>
      ) : null}

      {/* Rutas públicas clásicas con :empresaCodigo */}
      <Route path="/reclamos/:empresaCodigo" element={<PublicFormPage />} />
      <Route path="/consulta/:empresaCodigo" element={<ConsultaReclamoPage />} />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="reclamos" element={<ReclamosPage />} />
        <Route 
          path="resoluciones" 
          element={
            <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMINISTRADOR', 'LEGAL', 'SUPERVISOR', 'USUARIO']}>
              <ResolucionesPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="proyectos" 
          element={
            <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMINISTRADOR', 'SUPERVISOR']}>
              <ProyectosPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="usuarios" 
          element={
            <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMINISTRADOR']}>
              <UsuariosPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="empresa" 
          element={
            <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMINISTRADOR']}>
              <EmpresaPage />
            </ProtectedRoute>
          } 
        />
        <Route path="perfil" element={<PerfilPage />} />
        <Route path="notificaciones" element={<NotificacionesPage />} />
        <Route
          path="empresas"
          element={
            <ProtectedRoute allowedRoles={['SUPERADMIN']}>
              <EmpresasPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="auditoria"
          element={
            <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMINISTRADOR', 'SUPERVISOR']}>
              <AuditoriaPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="reclamos/:reclamoId/resolucion"
          element={
            <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMINISTRADOR', 'LEGAL', 'SUPERVISOR', 'USUARIO']}>
              <ResolucionPage />
            </ProtectedRoute>
          }
        />

        {/* ============================================================
            CENTRO DE AYUDA (Sistema de Tickets) — visible solo si la empresa
            tiene modulo_tickets_activo. La validación de visibilidad ocurre
            tanto en el sidebar como en cada página del módulo.
            ============================================================ */}
        <Route
          path="centro-ayuda/tickets"
          element={
            <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMINISTRADOR', 'SUPERVISOR', 'USUARIO', 'LEGAL']}>
              <CentroAyudaTickets />
            </ProtectedRoute>
          }
        />
        <Route
          path="centro-ayuda/tickets/:ticketId"
          element={
            <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMINISTRADOR', 'SUPERVISOR', 'USUARIO', 'LEGAL']}>
              <CentroAyudaTicketDetalle />
            </ProtectedRoute>
          }
        />
        <Route
          path="centro-ayuda/configuracion"
          element={
            <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMINISTRADOR']}>
              <CentroAyudaConfiguracion />
            </ProtectedRoute>
          }
        />
        <Route
          path="centro-ayuda/estadisticas"
          element={
            <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMINISTRADOR', 'SUPERVISOR']}>
              <CentroAyudaEstadisticas />
            </ProtectedRoute>
          }
        />
        <Route
          path="centro-ayuda/auditoria"
          element={
            <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMINISTRADOR', 'SUPERVISOR']}>
              <CentroAyudaAuditoria />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* 404 */}
      <Route
        path="*"
        element={
          <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
            <h1 className="font-heading text-4xl font-bold text-slate-900 mb-4">404</h1>
            <p className="text-slate-600 mb-6">Pagina no encontrada</p>
            <a href="/" className="text-[#1a1a2e] hover:underline">
              Volver al inicio
            </a>
          </div>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <EmpresaContextProvider>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" />
        </AuthProvider>
      </EmpresaContextProvider>
    </BrowserRouter>
  );
}

export default App;
